import type { StreamEffectStyle } from './stream-api';

export type NativeAlertType = 'follow' | 'subscribe' | 'resubscribe' | 'gift' | 'cheer' | 'raid' | 'shoutout' | 'redemption';
export type TestableAlertType = NativeAlertType;
export type AlertMotionMode = 'classic' | 'animated' | 'maximum';

export type AlertConfig = {
  enabled: boolean;
  captureAfter: string | null;
  channelPoints: {
    rewardId: string | null;
    rewardTitle: string | null;
    captureAfter: string | null;
    scopeReady: boolean;
    rewards: Array<{
      id: string;
      title: string;
      cost: number;
      enabled: boolean;
      paused: boolean;
      inStock: boolean;
    }>;
    lastError: string | null;
  };
  styles: Array<{ style: StreamEffectStyle; alertDurationMs: number; alertMotionMode: AlertMotionMode; ambientShimmerEnabled: boolean }>;
  slots: Array<{
    style: StreamEffectStyle;
    slot: NativeAlertType;
    soundId: string | null;
    soundName: string | null;
    volumeOverride: number | null;
  }>;
};

export type AlertStatus = {
  enabled: boolean;
  overlayClients: number;
  captureAfter: string | null;
  counts: {
    queued: number;
    playing: number;
    completed: number;
    failed: number;
    skipped: number;
  };
  overlayUrl: string;
};

export type RecentAlert = {
  id: string;
  twitch_event_id: string | null;
  source_alert_id: string | null;
  session_id: string | null;
  style: StreamEffectStyle;
  alert_type: NativeAlertType | 'viewer_sound';
  event_type: string;
  user_id: string | null;
  user_login: string | null;
  user_name: string | null;
  amount: number | string | null;
  metadata: Record<string, unknown>;
  status: string;
  queue_status: string;
  visual_status: string;
  audio_status: string | null;
  visual_error: string | null;
  audio_error: string | null;
  queued_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  duration_ms: number | string;
  created_at: string;
  updated_at: string;
  simulated: boolean;
};

export type AlertSound = {
  id: string;
  name: string;
  enabled: boolean;
  soundboardVisible: boolean;
};

export type AlertWorkspaceSnapshot = {
  selectedStyle: StreamEffectStyle;
  config: AlertConfig;
  status: AlertStatus;
  recent: RecentAlert[];
  sounds: AlertSound[];
  rehearsalEnabled: boolean;
  safeModeEnabled: boolean;
  suppressedFeatures: string[];
  rehearsalSuppressed: boolean;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function loadAlertsWorkspace(): Promise<AlertWorkspaceSnapshot> {
  const [effects, config, status, recent, profiles, foundation] = await Promise.all([
    request<{ selectedStyle: StreamEffectStyle }>('/api/v1/effects'),
    request<AlertConfig>('/api/v1/effects/alerts/config'),
    request<AlertStatus>('/api/v1/effects/alerts/status'),
    request<RecentAlert[]>('/api/v1/effects/alerts/recent'),
    request<{ sounds: AlertSound[] }>('/api/v1/effects/profiles'),
    request<{ streamSetup?: { modes?: { rehearsalEnabled?: boolean; safeModeEnabled?: boolean; suppressedFeatures?: string[] } } }>('/api/v1/foundation/overview'),
  ]);
  const modes = foundation.streamSetup?.modes;
  const suppressedFeatures = Array.isArray(modes?.suppressedFeatures)
    ? modes.suppressedFeatures.filter((value): value is string => typeof value === 'string')
    : [];
  return {
    selectedStyle: effects.selectedStyle,
    config,
    status,
    recent,
    sounds: profiles.sounds,
    rehearsalEnabled: modes?.rehearsalEnabled === true,
    safeModeEnabled: modes?.safeModeEnabled === true,
    suppressedFeatures,
    rehearsalSuppressed: modes?.safeModeEnabled === true && suppressedFeatures.includes('rehearsal-actions'),
  };
}

export async function loadAlertLiveState(): Promise<Pick<AlertWorkspaceSnapshot, 'status' | 'recent'>> {
  const [status, recent] = await Promise.all([
    request<AlertStatus>('/api/v1/effects/alerts/status'),
    request<RecentAlert[]>('/api/v1/effects/alerts/recent'),
  ]);
  return { status, recent };
}

export function updateAlertConfig(changes: {
  enabled?: boolean;
  style?: StreamEffectStyle;
  alertDurationMs?: number;
  alertMotionMode?: AlertMotionMode;
  ambientShimmerEnabled?: boolean;
  slot?: NativeAlertType;
  soundId?: string | null;
  volumeOverride?: number | null;
  redemptionRewardId?: string | null;
}): Promise<{ ok: true }> {
  return request('/api/v1/effects/alerts/config', { method: 'PUT', body: JSON.stringify(changes) });
}

export function updateRuntimeModes(modes: {
  rehearsalEnabled: boolean;
  safeModeEnabled: boolean;
  suppressedFeatures: string[];
}): Promise<{ ok: true }> {
  return request('/api/v1/foundation/modes', { method: 'PUT', body: JSON.stringify(modes) });
}

export function testAlert(type: TestableAlertType, userName = 'MutedFFS'): Promise<{ ok: boolean; alertId?: string }> {
  return request('/api/v1/effects/alerts/test', { method: 'POST', body: JSON.stringify({ type, userName }) });
}

export function replayAlert(alertId: string): Promise<{ ok: boolean; alertId: string; sourceAlertId: string; simulated: true }> {
  return request(`/api/v1/effects/alerts/${encodeURIComponent(alertId)}/replay`, { method: 'POST', body: '{}' });
}
